import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { ALL_CASES } from "@/data/caseStudies";
import CaseLogo from "@/components/CaseLogo";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

export default function CaseStudiesPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };

  return (
    <main data-testid="case-studies-page">
      <Seo
        title="Case Studies — Real Client Results | ZD Design"
        description="Nineteen real ZD Design case studies — HVAC, real estate, manufacturing, franchises and more: SEO, remarketing and website wins in the client's own words."
        path="/case-studies"
      />

      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-20 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Case studies</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              Results, client by client<span className="text-flame">.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              Nineteen case studies — SEO, remarketing, websites and strategy —
              told in the client's own words.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {ALL_CASES.map((c, i) => (
              <FadeUp key={c.slug} delay={(i % 3) * 0.08} className="h-full">
                <Link
                  to={`/case-studies/${c.slug}`}
                  data-testid={`case-studies-index-card-${c.slug}`}
                  className="glass flex h-full flex-col rounded-2xl border border-slate-200 p-7 transition-all duration-500 hover:-translate-y-1 hover:border-flame/40"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-base text-faint">{c.num}</span>
                    <ArrowRight className="h-4 w-4 text-flame transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                  <h2 className="mt-5 font-display text-xl font-bold tracking-tight text-slate-900">
                    {c.client}
                  </h2>
                  <div className="mt-1 font-mono text-[13px] uppercase tracking-[0.18em] text-flame">
                    {c.industry}
                  </div>
                  <CaseLogo slug={c.slug} client={c.client} className="mt-5" />
                  <p className="mt-4 flex-1 text-base leading-relaxed text-mist">
                    {c.after}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.2em] text-flame">
                    Read the full story
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.1}>
            <div className="mt-14 rounded-2xl border border-slate-200 bg-obsidian p-10 text-center md:p-14">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Want your business on this page?
              </h2>
              <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                Book a free strategy call — pick a time that suits you and
                talk directly with Todd.
              </p>
              <button
                data-testid="case-studies-index-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free strategy call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
